import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tabs,
    Tab,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    Box,
    MenuItem,
    Typography,
    Chip,
    InputAdornment,
    CircularProgress,
    Checkbox,
    Grid,
    Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { DesktopDatePicker } from "@mui/lab";
import {
    useCheckUserExistsQuery,
    useCreateSiteMutation,
    useCreateUserMutation,
    useGetSitesQuery,
} from "../../dataAccess";
import { addSiteUser, addUser, selectSites, setSites } from "../../userManagementSlice";
import { useDispatch, useSelector } from "react-redux";
import { SiteUser, User } from "../../types";
import theme from "../../../../app/theme";
import { useSnackbar } from "notistack";

const SITE_USER_ROLE = "Site User";

interface CreateUserModalProps {
    open: boolean;
    onClose: () => void;
    rolesList: string[];
    siteRolesList?: string[];
    countriesList?: { code: string; name: string }[];
    trialsList: { name: string; uuid: string }[];
    initialTab?: number;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
    open,
    onClose,
    rolesList,
    siteRolesList,
    countriesList,
    trialsList,
    initialTab = 0,
}) => {
    const dispatch = useDispatch();
    const [createUser] = useCreateUserMutation();
    const [createSite] = useCreateSiteMutation();
    const [tab, setTab] = useState(0);
    const { enqueueSnackbar } = useSnackbar();
    const getDefaultExpirationDate = () => {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 2);
        return date;
    };

    const [form, setForm] = useState({
        email: "",
        firstName: "",
        lastName: "",
        password: "",
        expires: getDefaultExpirationDate() as Date | null,
        activated: true,
        roles: [] as string[],
        trial: "" as string,
        site: "" as string,
        certificationRequired: false,
    });
    const [emailExists, setEmailExists] = useState<boolean>(false);
    const [shouldCheckEmail, setShouldCheckEmail] = useState<boolean>(false);
    const [emailChecked, setEmailChecked] = useState<boolean>(false);
    const [selectedTrialId, setSelectedTrialId] = useState<string>("");
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [emailFormatError, setEmailFormatError] = useState<boolean>(false);
    const [createSiteDrawerOpen, setCreateSiteDrawerOpen] = useState<boolean>(false);
    const [rolesSelectOpen, setRolesSelectOpen] = useState<boolean>(false);
    const [newSite, setNewSite] = useState<{ siteName: string; country: string; trialId: string }>({
        siteName: "",
        country: "",
        trialId: "",
    });

    const sites = useSelector(selectSites) ?? [];

    const {
        data: sitesData,
        isLoading: sitesLoading,
        error: sitesError,
    } = useGetSitesQuery(selectedTrialId, { skip: !form.trial || !selectedTrialId });

    const { data: usersData, isLoading: usersLoading } = useCheckUserExistsQuery(form.email, {
        skip: !shouldCheckEmail || !form.email,
    });

    useEffect(() => {
        if (usersData && typeof usersData === "object" && "exists" in usersData) {
            setEmailExists(usersData.exists);
            setEmailChecked(true);
        }

        if (sitesData && Array.isArray(sitesData)) {
            dispatch(setSites(sitesData));
        }
    }, [usersData, sitesData, sitesError, usersLoading, sitesLoading, dispatch]);

    useEffect(() => {
        setTab(initialTab);
        if (!open) {
            setForm({
                email: "",
                firstName: "",
                lastName: "",
                password: "",
                expires: getDefaultExpirationDate(),
                activated: true,
                roles: [],
                trial: "",
                site: "",
                certificationRequired: false,
            });
            setEmailExists(false);
            setShouldCheckEmail(false);
            setEmailChecked(false);
            setPasswordError(false);
            setEmailFormatError(false);
            setCreateSiteDrawerOpen(false);
            setNewSite({ siteName: "", country: "", trialId: "" });
            setSelectedTrialId("");
            setRolesSelectOpen(false);
        }
    }, [open, initialTab]);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name === "site" && value === "__CREATE_NEW_SITE__") {
            setCreateSiteDrawerOpen(true);
            return;
        }

        setForm({ ...form, [name]: value });
        if (name === "email") {
            setShouldCheckEmail(false);
            setEmailExists(false);
            setEmailChecked(false);
            setEmailFormatError(false);
        }
        if (name === "password") {
            setPasswordError(false);
        }
        if (name === "trial") {
            const trialId = trialsList.find((trial) => trial.name === value)?.uuid || "";
            setSelectedTrialId(trialId);
            setNewSite({ ...newSite, trialId });
        }
    };

    const handleNewSiteChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setNewSite({ ...newSite, [e.target.name]: e.target.value });
    };

    const handleOpenCreateSite = () => {
        if (!selectedTrialId) return;
        setCreateSiteDrawerOpen(true);
    };

    const handleCloseCreateSite = () => {
        setCreateSiteDrawerOpen(false);
        setNewSite({ siteName: "", country: "", trialId: selectedTrialId || "" });
    };

    const generateTempId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

    const handleConfirmCreateSite = async () => {
        if (!newSite.siteName || !newSite.country || !newSite.trialId) return;
        try {
            const createdNewSite = await createSite({
                name: newSite.siteName,
                countryCode: newSite.country,
                trialId: newSite.trialId,
            }).unwrap();
            dispatch(setSites([...sites, createdNewSite]));
            setForm({ ...form, site: newSite.siteName });
            setCreateSiteDrawerOpen(false);
            setNewSite({ siteName: "", country: "", trialId: selectedTrialId || "" });
        } catch (error) {
            const errorMessage = (error as any).data?.message || "Something went wrong";
            enqueueSnackbar(errorMessage, { variant: "error" });
        }
    };

    const handleEmailBlur = () => {
        if (form.email) {
            const isValidFormat = validateEmail(form.email);
            setEmailFormatError(!isValidFormat);
            if (isValidFormat) {
                setShouldCheckEmail(true);
            }
        }
    };

    const handlePasswordBlur = () => {
        if (form.password) {
            setPasswordError(form.password.length < 8);
        }
    };

    const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const roles = typeof value === "string" ? value.split(",") : (value as unknown as string[]);
        const hadSiteUserRole = form.roles.includes(SITE_USER_ROLE);
        const hasSiteUserRole = roles.includes(SITE_USER_ROLE);
        setForm({
            ...form,
            roles,
            ...(tab === 1 && hasSiteUserRole !== hadSiteUserRole && { certificationRequired: hasSiteUserRole }),
        });
    };

    const handleChangeDate = (date: Date | null) => {
        setForm({ ...form, expires: date });
    };

    const handleCreateUser = async () => {
        const newDataUser = {
            email: form.email,
            firstName: form.firstName,
            lastName: form.lastName,
            accessExpires: form.expires as Date,
            activated: form.activated,
            roles: form.roles,
            affiliationType: (tab === 0 ? "trial" : "site") as "trial" | "site",
            trialName: form.trial,
            siteName: form.site,
            password: form.password,
            ...(tab === 1 && { certificationRequired: form.certificationRequired }),
        };
        const result = await createUser(newDataUser);
        if (result && "data" in result) {
            if (tab === 0) {
                dispatch(addUser(result.data as User));
            } else {
                const newUserData = result.data;
                const userSiteData = {
                    ...newUserData.site,
                    trial: {
                        trialName: newUserData.trial.trialName,
                        uuid: newUserData.trial.uuid,
                    },
                };

                const newSiteUser = {
                    ...result.data,
                    sites: [userSiteData],
                };

                dispatch(addSiteUser(newSiteUser as SiteUser));
            }

            onClose();
            setForm({
                email: "",
                firstName: "",
                lastName: "",
                password: "",
                expires: getDefaultExpirationDate(),
                activated: true,
                roles: [],
                trial: "",
                site: "",
                certificationRequired: false,
            });
            setEmailExists(false);
            setShouldCheckEmail(false);
            setEmailChecked(false);
            setPasswordError(false);
            setEmailFormatError(false);
            setCreateSiteDrawerOpen(false);
            setNewSite({ siteName: "", country: "", trialId: "" });
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { position: "relative", overflow: "hidden" } }}
            >
                <DialogTitle sx={{ bgcolor: theme.palette.background.paper, color: "#fff", pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6">{"Create New User"}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                {"Add a new user or site user to the clinical trial management system"}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{
                        bgcolor: theme.palette.background.paper,
                        color: "#fff",
                        pt: 4,
                        pb: 3,
                    }}
                >
                    {/* Dummy inputs to fool browser autofill */}
                    <Box sx={{ width: 0, height: 0, overflow: "hidden" }}>
                        <input type="text" name="fakeusernameremembered" tabIndex={-1} />
                        <input type="password" name="fakepasswordremembered" tabIndex={-1} />
                    </Box>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => {
                            setTab(v);
                            setForm({ ...form, roles: [], certificationRequired: false });
                            setRolesSelectOpen(false);
                        }}
                        variant="fullWidth"
                        sx={{
                            "mb": 3.5,
                            "bgcolor": theme.palette.background.paper,
                            "borderRadius": 1,
                            "& .MuiTab-root": { color: "#aaa" },
                            "& .Mui-selected": { color: "#fff" },
                        }}
                    >
                        <Tab label="Regular User" />
                        <Tab label="Site User" />
                    </Tabs>
                    <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 600, mb: 2 }}>
                        PERSONAL INFORMATION
                    </Typography>
                    <Grid container spacing={2} mb={20} mt={10} alignItems="flex-start">
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                onBlur={handleEmailBlur}
                                fullWidth
                                size="small"
                                inputProps={{ autoComplete: "new-password" }}
                                error={emailFormatError || emailExists}
                                InputProps={{
                                    endAdornment: (shouldCheckEmail || emailChecked) && !emailFormatError && (
                                        <InputAdornment position="end">
                                            {usersLoading ? (
                                                <CircularProgress size={20} sx={{ color: "#4caf50" }} />
                                            ) : emailChecked ? (
                                                !emailExists ? (
                                                    <CheckCircleIcon sx={{ color: "#4caf50" }} />
                                                ) : (
                                                    <CancelIcon sx={{ color: "#f44336" }} />
                                                )
                                            ) : null}
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    bgcolor: theme.palette.background.paper,
                                    input: { color: "#fff" },
                                    label: { color: "#aaa" },
                                }}
                            />
                            {emailFormatError && (
                                <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: "0.75rem" }}>
                                    Please enter a valid email address.
                                </Typography>
                            )}
                            {!emailFormatError && emailExists && (
                                <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: "0.75rem" }}>
                                    This email is already associated with an existing user.
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="First Name"
                                name="firstName"
                                value={form.firstName}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                inputProps={{ autoComplete: "new-password" }}
                                sx={{
                                    bgcolor: theme.palette.background.paper,
                                    input: { color: "#fff" },
                                    label: { color: "#aaa" },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Last Name"
                                name="lastName"
                                value={form.lastName}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                inputProps={{ autoComplete: "new-password" }}
                                sx={{
                                    flex: 1,
                                    bgcolor: theme.palette.background.paper,
                                    input: { color: "#fff" },
                                    label: { color: "#aaa" },
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 600, mb: 2, mt: 3 }}>
                        CREDENTIALS & SECURITY
                    </Typography>
                    <Grid container spacing={2} mb={10} mt={10} alignItems="flex-start">
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Password"
                                name="devicePassword"
                                type="text"
                                value={form.password}
                                onChange={(e) =>
                                    handleChange({
                                        ...e,
                                        target: { ...e.target, name: "password", value: e.target.value },
                                    })
                                }
                                onBlur={handlePasswordBlur}
                                fullWidth
                                size="small"
                                error={passwordError}
                                inputProps={{
                                    autoComplete: "new-password",
                                    style: { WebkitTextSecurity: "disc" } as any,
                                }}
                                sx={{
                                    bgcolor: theme.palette.background.paper,
                                    input: { color: "#fff" },
                                    label: { color: "#aaa" },
                                }}
                            />
                            {passwordError && (
                                <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: "0.75rem" }}>
                                    Password must be at least 8 characters long.
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DesktopDatePicker
                                value={form.expires}
                                onChange={(newDate: Date | null) => handleChangeDate(newDate)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Access Expires"
                                        name="expires"
                                        fullWidth
                                        size="small"
                                        sx={{
                                            bgcolor: theme.palette.background.paper,
                                            input: { color: "#fff" },
                                            label: { color: "#aaa" },
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            autoComplete: "new-password",
                                            readOnly: true,
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Box mb={10}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.activated}
                                    onChange={(_, checked) => setForm({ ...form, activated: checked })}
                                    color="success"
                                />
                            }
                            label="Activated"
                            sx={{ color: "#fff" }}
                        />
                    </Box>

                    <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 600, mb: 2, mt: 3 }}>
                        PERMISSIONS
                    </Typography>
                    <Box mb={10}>
                        <TextField
                            select
                            label="Roles"
                            name="roles"
                            value={form.roles}
                            onChange={handleRoleChange}
                            fullWidth
                            size="small"
                            SelectProps={{
                                multiple: true,
                                open: rolesSelectOpen,
                                onOpen: () => setRolesSelectOpen(true),
                                onClose: () => setRolesSelectOpen(false),
                                renderValue: (selected) => {
                                    const selectedArray = selected as string[];
                                    if (!selectedArray || selectedArray.length === 0) {
                                        return "Select roles";
                                    }
                                    return (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {selectedArray.map((value) => (
                                                <Chip
                                                    key={value}
                                                    label={value}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: "4px",
                                                        border: "1px solid #4caf50",
                                                        bgcolor: "rgba(76, 175, 80, 0.15)",
                                                        color: "#4caf50",
                                                        height: "24px",
                                                        fontSize: "0.75rem",
                                                        marginX: "2px",
                                                        marginBottom: "2px",
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    );
                                },
                            }}
                            sx={{ flex: 1, bgcolor: theme.palette.background.paper, label: { color: "#aaa" } }}
                        >
                            {(tab === 0 ? rolesList : siteRolesList || []).map((role, index) => (
                                <MenuItem
                                    key={`role-${index}`}
                                    value={role}
                                    sx={{ color: "#fff", bgcolor: theme.palette.background.paper }}
                                >
                                    <Checkbox
                                        checked={form.roles.indexOf(role) > -1}
                                        sx={{
                                            "color": "#aaa",
                                            "&.Mui-checked": {
                                                color: "#4caf50",
                                            },
                                        }}
                                    />
                                    {typeof role === "string" ? role : String(role)}
                                </MenuItem>
                            ))}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    p: 1,
                                    borderTop: "1px solid #444",
                                    bgcolor: theme.palette.background.paper,
                                    position: "sticky",
                                    bottom: 0,
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => setRolesSelectOpen(false)}
                                    sx={{ minWidth: 100 }}
                                >
                                    Ok
                                </Button>
                            </Box>
                        </TextField>
                    </Box>

                    <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 600, mb: 2, mt: 3 }}>
                        TRIAL ASSIGNMENT
                    </Typography>
                    <Box mb={10}>
                        <TextField
                            select
                            label="Trial"
                            name="trial"
                            value={form.trial}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={{ flex: 1, bgcolor: theme.palette.background.paper, label: { color: "#aaa" } }}
                        >
                            {trialsList.map((trial, index) => (
                                <MenuItem
                                    key={`${trial.name}-${index}`}
                                    value={trial.name}
                                    sx={{ color: "#fff", bgcolor: theme.palette.background.paper }}
                                >
                                    {trial.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    {tab === 1 && (
                        <Box mb={10}>
                            <TextField
                                select
                                label="Site"
                                name="site"
                                value={form.site}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputProps={{ autoComplete: "new-password" }}
                                sx={{ bgcolor: theme.palette.background.paper, label: { color: "#aaa" } }}
                                disabled={form.trial === "" || sitesLoading}
                            >
                                {[...sites]
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((site, index) => (
                                        <MenuItem
                                            key={`site-${index}`}
                                            value={site.name}
                                            sx={{ color: "#fff", bgcolor: theme.palette.background.paper }}
                                        >
                                            {site.name}
                                        </MenuItem>
                                    ))}
                                <MenuItem
                                    value="__CREATE_NEW_SITE__"
                                    sx={{
                                        color: "#4caf50",
                                        bgcolor: theme.palette.background.paper,
                                        fontStyle: "italic",
                                    }}
                                >
                                    Create site...
                                </MenuItem>
                            </TextField>
                        </Box>
                    )}
                    {tab === 1 && (
                        <Box mb={10}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="certificationRequired"
                                        checked={form.certificationRequired}
                                        onChange={(_, checked) => setForm({ ...form, certificationRequired: checked })}
                                        sx={{
                                            "color": "#aaa",
                                            "&.Mui-checked": { color: "#4caf50" },
                                        }}
                                    />
                                }
                                label="Requires certification"
                                sx={{ color: "#fff" }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        pointerEvents: createSiteDrawerOpen ? "auto" : "none",
                        zIndex: 10,
                    }}
                >
                    {createSiteDrawerOpen && (
                        <Box
                            onClick={handleCloseCreateSite}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                bgcolor: "rgba(0,0,0,0.45)",
                                zIndex: 1,
                            }}
                        />
                    )}
                    <Slide direction="left" in={createSiteDrawerOpen} mountOnEnter unmountOnExit>
                        <Box
                            sx={{
                                width: { xs: 320, sm: 380 },
                                bgcolor: theme.palette.background.paper,
                                maxHeight: "90%",
                                height: "auto",
                                boxShadow: 8,
                                borderLeft: "1px solid #333",
                                display: "flex",
                                flexDirection: "column",
                                pointerEvents: "auto",
                                zIndex: 2,
                                overflowY: "auto",
                                borderRadius: 1,
                            }}
                        >
                            <Box sx={{ p: 16, borderBottom: "1px solid #333" }}>
                                <Typography variant="h6" sx={{ color: "#fff" }}>
                                    Create new site
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#aaa", mt: 4 }}>
                                    Add a site to the selected trial
                                </Typography>
                            </Box>
                            <Box sx={{ p: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                                <TextField
                                    label="Site Name"
                                    name="siteName"
                                    value={newSite.siteName}
                                    onChange={handleNewSiteChange}
                                    autoComplete="new-password"
                                    size="small"
                                    fullWidth
                                    sx={{ bgcolor: theme.palette.background.paper, label: { color: "#aaa" } }}
                                />
                                {countriesList && countriesList.length > 0 ? (
                                    <TextField
                                        select
                                        label="Country"
                                        name="country"
                                        value={newSite.country}
                                        onChange={handleNewSiteChange}
                                        size="small"
                                        fullWidth
                                        sx={{ bgcolor: theme.palette.background.paper, label: { color: "#aaa" } }}
                                    >
                                        {countriesList.map((country) => (
                                            <MenuItem
                                                key={country.code}
                                                value={country.code}
                                                sx={{ color: "#fff", bgcolor: theme.palette.background.paper }}
                                            >
                                                {country.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        label="Country"
                                        name="country"
                                        value={newSite.country}
                                        onChange={handleNewSiteChange}
                                        size="small"
                                        fullWidth
                                        autoComplete="new-password"
                                        sx={{ bgcolor: theme.palette.background.paper, label: { color: "#aaa" } }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ mt: "auto", p: 16, display: "flex", gap: 8, borderTop: "1px solid #333" }}>
                                <Button onClick={handleCloseCreateSite}>Cancel</Button>
                                <Button
                                    variant="contained"
                                    onClick={handleConfirmCreateSite}
                                    disabled={!newSite.siteName || !newSite.country || !selectedTrialId}
                                >
                                    Create site
                                </Button>
                            </Box>
                        </Box>
                    </Slide>
                </Box>
                <DialogActions sx={{ bgcolor: theme.palette.background.paper, py: 16, px: 24 }}>
                    <Button onClick={onClose} sx={{ mr: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateUser}
                        disabled={
                            emailExists ||
                            emailFormatError ||
                            !form.email ||
                            !form.firstName ||
                            !form.lastName ||
                            !form.password ||
                            !form.expires ||
                            passwordError ||
                            form.password.length < 8 ||
                            form.roles.length === 0 ||
                            form.trial === "" ||
                            (tab === 1 && !form.site)
                        }
                    >
                        Create User
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CreateUserModal;
